import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Table, Tag, Col, DatePicker } from 'antd';
import StoreSelect from '../../../components/common/StoreSelect';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import type { WaveConfigLogDTO, QueryWaveConfigLogPageRequest } from '../../../types/wave/waveConfigLog';
import type { Paging } from '../../../types/common';
import * as waveConfigLogService from '../../../services/wave/waveConfigLogService';
import { WaveRuleStatusOptions } from '../../../constants/wave';

const WaveConfigLogList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<WaveConfigLogDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // Load data
  const loadData = async (params: QueryWaveConfigLogPageRequest) => {
    setLoading(true);
    try {
      const response = await waveConfigLogService.pageWaveConfigLogs(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      }
    } catch (error) {
      // Error handled by service
    } finally {
      setLoading(false);
    }
  };

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // Search
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const params: QueryWaveConfigLogPageRequest = {
      currentPage: 1,
      pageSize: pagination.pageSize,
      storeCode: values.storeCode,
      ruleCode: values.ruleCode,
      status: values.status,
      gmtCreateStart: values.gmtCreateRange?.[0]?.startOf('day').valueOf(),
      gmtCreateEnd: values.gmtCreateRange?.[1]?.endOf('day').valueOf(),
    };
    loadData(params);
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Pagination change
  const handlePageChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    const params: QueryWaveConfigLogPageRequest = {
      currentPage: page,
      pageSize,
      storeCode: values.storeCode,
      ruleCode: values.ruleCode,
      status: values.status,
      gmtCreateStart: values.gmtCreateRange?.[0]?.startOf('day').valueOf(),
      gmtCreateEnd: values.gmtCreateRange?.[1]?.endOf('day').valueOf(),
    };
    loadData(params);
  };

  // Format date
  const formatDate = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD') : '-';
  };

  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: string) => {
    const option = WaveRuleStatusOptions.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // Render picking mode
  const renderPickingMode = (mode: string) => {
    if (mode === 'MANUAL_PICKING') {
      return t('wave.waveRule.form.fields.manualPicking');
    } else if (mode === 'ORDER_PICKING') {
      return t('wave.waveRule.form.fields.orderPicking');
    } else if (mode === 'BATCH_PICKING') {
      return t('wave.waveRule.form.fields.batchPicking');
    }
    return mode || '-';
  };

  // Table columns
  const columns = [
    {
      title: t('wave.waveConfigLog.list.table.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      width: 120,
    },
    {
      title: t('wave.waveConfigLog.list.table.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: 200,
    },
    {
      title: t('wave.waveConfigLog.list.table.ruleCode'),
      dataIndex: 'ruleCode',
      key: 'ruleCode',
      width: 140,
    },
    {
      title: t('wave.waveConfigLog.list.table.ruleName'),
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 180,
    },
    {
      title: t('wave.waveConfigLog.list.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatusTag,
    },
    {
      title: t('wave.waveConfigLog.list.table.gmtCreate'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('wave.waveConfigLog.list.table.executionMode'),
      dataIndex: 'executionMode',
      key: 'executionMode',
      width: 120,
      render: renderPickingMode,
    },
    {
      title: t('wave.waveConfigLog.list.table.effectiveStartDate'),
      dataIndex: 'effectiveStartDate',
      key: 'effectiveStartDate',
      width: 120,
      render: formatDate,
    },
    {
      title: t('wave.waveConfigLog.list.table.effectiveEndDate'),
      dataIndex: 'effectiveEndDate',
      key: 'effectiveEndDate',
      width: 120,
      render: (value: number | null) => value ? formatDate(value) : t('common.permanent'),
    },
  ];

  // Search form fields
  const renderSearchFields = () => (
    <>
      <Col span={5}>
        <Form.Item name="storeCode" label={t('wave.waveConfigLog.list.search.storeCode')}>
          <StoreSelect placeholder={t('wave.waveConfigLog.list.search.placeholder.storeCode')} allowClear />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item name="ruleCode" label={t('wave.waveConfigLog.list.search.ruleCode')}>
          <Input
            placeholder={t('wave.waveConfigLog.list.search.placeholder.ruleCode')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item name="status" label={t('wave.waveConfigLog.list.search.status')}>
          <Select
            placeholder={t('wave.waveConfigLog.list.search.placeholder.status')}
            allowClear
            options={WaveRuleStatusOptions.map(opt => ({
              label: t(opt.label),
              value: opt.value,
            }))}
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item name="gmtCreateRange" label={t('wave.waveConfigLog.list.search.gmtCreateRange')}>
          <DatePicker.RangePicker
            placeholder={[
              t('wave.waveConfigLog.list.search.placeholder.startDate'),
              t('wave.waveConfigLog.list.search.placeholder.endDate'),
            ]}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>
    </>
  );

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Search Panel */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        {renderSearchFields()}
      </FilterPanel>

      {/* Table */}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        scroll={{ x: 1300, y: 'calc(100vh - 380px)' }}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => t('common.pagination.total', { total }),
          onChange: handlePageChange,
        }}
      />
    </div>
  );
};

export default WaveConfigLogList;
